import React, { useState } from 'react';
import './CanineHealthCheck.css';
import { apiService } from '../services/api';

interface HealthCheckResult {
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  urgencyLevel: 'immediate' | 'urgent' | 'monitor' | 'routine';
  primaryConcerns: string[];
  detectedToxins: string[];
  analysis: string;
  recommendations: string[];
  emergencyActions: string[];
  vetAdvice: string;
  followUpInstructions: string;
  disclaimer: string;
}

interface CanineHealthCheckProps {
  onClose: () => void;
}

const CanineHealthCheck: React.FC<CanineHealthCheckProps> = ({ onClose }) => {
  const [symptoms, setSymptoms] = useState('');
  const [result, setResult] = useState<HealthCheckResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const analyzeSymptoms = async () => {
    if (!symptoms.trim()) return;

    setIsAnalyzing(true);
    
    try {
      // Create a specialized health check prompt
      const healthCheckPrompt = `CANINE HEALTH ASSESSMENT

Analyze the following dog symptoms and provide a balanced, professional health assessment:

SYMPTOMS REPORTED: "${symptoms}"

Provide a detailed analysis in the following JSON format:
{
  "riskLevel": "low|medium|high|critical",
  "urgencyLevel": "immediate|urgent|monitor|routine",
  "primaryConcerns": ["concern1", "concern2"],
  "detectedToxins": ["toxin1", "toxin2"] (if any toxic substances mentioned),
  "analysis": "Detailed professional analysis of the symptoms",
  "recommendations": ["action1", "action2", "action3"],
  "emergencyActions": ["emergency step 1", "emergency step 2"] (ONLY if truly life-threatening),
  "vetAdvice": "When and why to contact a veterinarian",
  "followUpInstructions": "What to monitor and next steps",
  "disclaimer": "Professional disclaimer about veterinary consultation"
}

URGENCY LEVEL GUIDELINES (be measured and appropriate):
- "immediate": ONLY for life-threatening emergencies (severe difficulty breathing, collapse, seizures, unconsciousness, severe trauma, confirmed toxic ingestion with symptoms)
- "urgent": Concerning symptoms requiring same-day vet visit (persistent vomiting/diarrhea with dehydration signs, moderate pain, suspected toxin exposure, blood in stool/urine)
- "monitor": Mild symptoms that should be watched (single episode of diarrhea, mild lethargy, decreased appetite for <24hrs, minor scratching)
- "routine": Schedule regular vet visit (general health questions, preventive care, mild ongoing issues)

CRITICAL GUIDELINES:
- Be BALANCED and MEASURED in urgency assessment - avoid unnecessary alarm
- Common symptoms like diarrhea + lethargy are "urgent" (same-day vet), NOT "immediate" unless severe dehydration/collapse
- Blood in stool alone is "urgent" (same-day vet), NOT "immediate" unless accompanied by collapse/severe symptoms
- If ANY confirmed toxic substances (chocolate, grapes, xylitol, onions, garlic, etc.) with symptoms are mentioned, set riskLevel to "high" and urgencyLevel to "immediate"
- For suspected toxin exposure without symptoms, use "urgent" (same-day vet)
- Reserve "immediate" and emergencyActions for TRUE life-threatening situations
- Always recommend veterinary consultation but match urgency to severity
- Provide specific, actionable advice appropriate to the urgency level
- Be professional, compassionate, and reassuring while still emphasizing proper care`;

      const response = await apiService.sendMessage({
        message: healthCheckPrompt,
        useWebSearch: false
      });

      // Parse the LLM response to extract JSON
      let analysisResult: HealthCheckResult;
      
      try {
        // Try to extract JSON from the response
        const jsonMatch = response.message.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          analysisResult = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found in response');
        }
      } catch (parseError) {
        console.error('Failed to parse LLM response:', parseError);
        // Fallback to a safe default response
        analysisResult = {
          riskLevel: 'medium',
          urgencyLevel: 'monitor',
          primaryConcerns: ['Symptoms require professional evaluation'],
          detectedToxins: [],
          analysis: 'Unable to fully analyze symptoms. The described symptoms should be evaluated by a veterinary professional for proper diagnosis and treatment.',
          recommendations: [
            'Monitor your dog closely',
            'Document any changes in symptoms',
            'Contact your veterinarian for professional advice'
          ],
          emergencyActions: [],
          vetAdvice: 'Consult with a veterinarian for proper diagnosis and treatment plan.',
          followUpInstructions: 'Keep monitoring your dog and seek professional veterinary care if symptoms persist or worsen.',
          disclaimer: 'This assessment is for informational purposes only and does not replace professional veterinary diagnosis and treatment.'
        };
      }

      setResult(analysisResult);
    } catch (error) {
      console.error('Health check analysis failed:', error);
      
      // Provide a safe fallback response
      setResult({
        riskLevel: 'medium',
        urgencyLevel: 'urgent',
        primaryConcerns: ['Unable to analyze symptoms - seek professional help'],
        detectedToxins: [],
        analysis: 'We encountered an issue analyzing the symptoms. For your dog\'s safety, please consult with a veterinary professional immediately.',
        recommendations: [
          'Contact your veterinarian immediately',
          'Monitor your dog closely',
          'Do not wait if symptoms worsen'
        ],
        emergencyActions: [
          'Call your emergency veterinary clinic',
          'Prepare to transport your dog if needed'
        ],
        vetAdvice: 'Immediate veterinary consultation is recommended due to system analysis limitations.',
        followUpInstructions: 'Seek professional veterinary care as soon as possible.',
        disclaimer: 'This system experienced technical difficulties. Always consult a veterinary professional for proper medical advice.'
      });
    }

    setIsAnalyzing(false);
  };

  const getRiskBadge = (level: string) => {
    switch (level) {
      case 'critical':
        return { emoji: '🚨', text: 'CRITICAL', class: 'risk-critical' };
      case 'high':
        return { emoji: '🔴', text: 'HIGH RISK', class: 'risk-high' };
      case 'medium':
        return { emoji: '🟡', text: 'MEDIUM RISK', class: 'risk-medium' };
      case 'low':
        return { emoji: '🟢', text: 'LOW RISK', class: 'risk-low' };
      default:
        return { emoji: '⚪', text: 'UNKNOWN', class: 'risk-unknown' };
    }
  };

  const getUrgencyBadge = (level: string) => {
    switch (level) {
      case 'immediate':
        return { emoji: '', text: 'IMMEDIATE ACTION', class: 'urgency-immediate' };
      case 'urgent':
        return { emoji: '', text: 'URGENT', class: 'urgency-urgent' };
      case 'monitor':
        return { emoji: '', text: 'MONITOR CLOSELY', class: 'urgency-monitor' };
      case 'routine':
        return { emoji: '', text: 'ROUTINE CARE', class: 'urgency-routine' };
      default:
        return { emoji: '', text: 'ASSESS', class: 'urgency-unknown' };
    }
  };

  return (
    <div className="health-check-overlay">
      <div className="health-check-modal">
        <div className="health-check-header">
          <h2>Canine Health Check</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="health-check-content">
          {!result ? (
            <div className="input-section">
              <label htmlFor="symptoms">Describe your dog's symptoms:</label>
              <textarea
                id="symptoms"
                value={symptoms}
                onChange={(e) => setSymptoms(e.target.value)}
                placeholder="e.g., my dog is vomiting after eating chocolate, seems lethargic and won't eat..."
                rows={4}
                disabled={isAnalyzing}
              />
              
              <button 
                className="analyze-button"
                onClick={analyzeSymptoms}
                disabled={!symptoms.trim() || isAnalyzing}
              >
                {isAnalyzing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-stethoscope"></i>
                    Analyze Symptoms
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="result-section">
              {/* Risk and Urgency Badges */}
              <div className="assessment-badges">
                <div className={`risk-badge ${getRiskBadge(result.riskLevel).class}`}>
                  <span className="badge-emoji">{getRiskBadge(result.riskLevel).emoji}</span>
                  <div className="badge-content">
                    <span className="badge-label">Risk Level</span>
                    <span className="badge-text">{getRiskBadge(result.riskLevel).text}</span>
                  </div>
                </div>

                <div className={`urgency-badge ${getUrgencyBadge(result.urgencyLevel).class}`}>
                  <span className="badge-emoji">{getUrgencyBadge(result.urgencyLevel).emoji}</span>
                  <div className="badge-content">
                    <span className="badge-label">Action Needed</span>
                    <span className="badge-text">{getUrgencyBadge(result.urgencyLevel).text}</span>
                  </div>
                </div>
              </div>

              {/* Emergency Actions - Show first if present */}
              {result.emergencyActions.length > 0 && (
                <div className="emergency-section">
                  <h3>🚨 Immediate Emergency Actions</h3>
                  <div className="emergency-actions">
                    {result.emergencyActions.map((action, index) => (
                      <div key={index} className="emergency-action">
                        <span className="action-number">{index + 1}</span>
                        <span className="action-text">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detected Toxins */}
              {result.detectedToxins.length > 0 && (
                <div className="detected-toxins">
                  <h3>Toxic Substances Detected</h3>
                  <div className="toxin-list">
                    {result.detectedToxins.map((toxin, index) => (
                      <span key={index} className="toxin-item">
                        🚨 {toxin}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Primary Concerns */}
              {result.primaryConcerns.length > 0 && (
                <div className="concerns-section">
                  <h3>🔍 Primary Concerns</h3>
                  <div className="concerns-list">
                    {result.primaryConcerns.map((concern, index) => (
                      <div key={index} className="concern-item">
                        <i className="fas fa-exclamation-triangle"></i>
                        <span>{concern}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Professional Analysis */}
              <div className="analysis-section">
                <h3>📋 Professional Analysis</h3>
                <p className="analysis-text">{result.analysis}</p>
              </div>

              {/* Recommendations */}
              {result.recommendations.length > 0 && (
                <div className="recommendations-section">
                  <h3>Recommended Actions</h3>
                  <div className="recommendations-list">
                    {result.recommendations.map((recommendation, index) => (
                      <div key={index} className="recommendation-item">
                        <i className="fas fa-check-circle"></i>
                        <span>{recommendation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Veterinary Advice */}
              <div className="vet-advice-section">
                <h3>🩺 Veterinary Consultation</h3>
                <p className="vet-advice">{result.vetAdvice}</p>
              </div>

              {/* Follow-up Instructions */}
              <div className="followup-section">
                <h3>📝 Follow-up Instructions</h3>
                <p className="followup-text">{result.followUpInstructions}</p>
              </div>

              {/* Disclaimer */}
              <div className="disclaimer">
                <h4>Important Disclaimer</h4>
                <p>{result.disclaimer}</p>
              </div>

              <button 
                className="new-check-button"
                onClick={() => {
                  setResult(null);
                  setSymptoms('');
                }}
              >
                <i className="fas fa-plus"></i>
                New Health Check
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CanineHealthCheck;
